import { ClerkProvider } from '@clerk/react';
import { dark } from '@clerk/themes';
import { useEffect, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { selectTheme } from '@/store/features/theme.feature';
import { BRAND } from '@/lib/brand';

const publishableKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ||
  'pk_test_bWludC1jb25kb3ItNzQuY2xlcmsuYWNjb3VudHMuZGV2JA';

if (!publishableKey) {
  throw new Error(
    'Clerk Publishable Key missing and must be set in environment variables'
  );
}

// Clerk fills its "continue to …" lines with the instance's name, Cleanlist.ai.
// On Clu (app.clugtm.com, and Canada's sign-in on app.cleanlist.ai) these name
// Clu instead; on Cleanlist, Clerk's own text is left alone.
const IS_CLU = BRAND.id === 'clu';
const CONTINUE_TO_CLU = { subtitle: `to continue to ${BRAND.dotName}` };
const WITH_PROVIDER_ON_CLU = (verb: string) => ({
  title: `${verb} to ${BRAND.dotName} with {{provider}}`,
});

// app.clugtm.com is a Clerk satellite of cleanlist.ai (same users, same
// production key). A satellite can't host sign-in: Clerk sends users to the
// primary's pages and back. Exact host only, so the app.clugtm.localhost brand
// preview keeps signing in locally with the dev key.
// The primary, in turn, must allow redirects back to the satellite. Setting
// allowedRedirectOrigins replaces Clerk's defaults (this origin and
// *.cleanlist.ai), so they are listed again, and only on production.
export const CLU_APP_ORIGIN = 'https://app.clugtm.com';
export const CLERK_PRIMARY_ORIGIN = 'https://app.cleanlist.ai';
const ORIGIN = typeof window === 'undefined' ? '' : window.location.origin;
export const IS_CLERK_SATELLITE = ORIGIN === CLU_APP_ORIGIN;
const SATELLITE_PROPS = IS_CLERK_SATELLITE
  ? ({
      isSatellite: true,
      // The satellite is registered in Clerk as clugtm.com, so its
      // Frontend API is clerk.clugtm.com.
      domain: 'clugtm.com',
      signInUrl: `${CLERK_PRIMARY_ORIGIN}/auth/login`,
      signUpUrl: `${CLERK_PRIMARY_ORIGIN}/auth/register`,
    } as const)
  : ORIGIN === CLERK_PRIMARY_ORIGIN
    ? {
        allowedRedirectOrigins: [
          ORIGIN,
          'https://cleanlist.ai',
          'https://*.cleanlist.ai',
          CLU_APP_ORIGIN,
        ],
      }
    : {};

// Custom dark theme for Clerk - uses #1a1a1a to match app dark mode
const customDarkTheme = Object.assign({}, dark, {
  elements: {
    card: 'bg-[#1a1a1a]!',
    cardBox: 'bg-[#1a1a1a]!',
    headerTitle: 'text-slate-100!',
    headerSubtitle: 'text-slate-400!',
    socialButtonsBlockButton:
      'bg-[#222]! border-white/6! hover:!bg-white/[0.08] text-slate-200!',
    formFieldLabel: 'text-slate-300!',
    formFieldInput: 'bg-[#222]! border-white/10! text-slate-100!',
    formButtonPrimary: '!bg-primary hover:bg-primary/90!',
    footerActionLink: 'text-primary! hover:text-primary/80!',
    footerActionText: 'text-slate-400!',
    navbar: 'bg-[#1a1a1a]! border-white/6!',
    navbarButton: 'text-slate-300! hover:!bg-white/[0.08]',
    navbarButtonIcon: 'text-slate-400!',
    pageScrollBox: 'bg-[#1a1a1a]!',
    page: 'bg-[#1a1a1a]!',
    scrollBox: 'bg-[#1a1a1a]!',
    rootBox: 'bg-[#1a1a1a]!',
    footer: 'bg-[#1a1a1a]! border-white/6!',
  },
});

const CLERK_DARK_OVERRIDE_CSS = `
  .cl-rootBox, .cl-cardBox, .cl-card, .cl-modalContent,
  .cl-scrollBox, .cl-pageScrollBox, .cl-page,
  .cl-navbar, .cl-footer, .cl-navbarMobileMenuRow { background-color: #1a1a1a !important; background-image: none !important; }
  .cl-navbar { color: #e2e8f0 !important; border-color: rgba(255,255,255,0.06) !important; }
  .cl-navbar * { border-color: rgba(255,255,255,0.06) !important; }
  .cl-navbar h1 { color: #ffffff !important; }
  .cl-navbar p { color: #94a3b8 !important; }
  .cl-navbarButton { color: #94a3b8 !important; background-color: transparent !important; }
  .cl-navbarButton:hover { background-color: rgba(255,255,255,0.06) !important; color: #e2e8f0 !important; }
  .cl-navbarButton[data-active="true"] { background-color: rgba(255,255,255,0.1) !important; color: #ffffff !important; }
  .cl-navbarButton[data-active="true"] .cl-navbarButtonIcon { color: #e2e8f0 !important; }
  .cl-navbarButtonIcon { color: #64748b !important; }
  .cl-profileSectionTitle { border-color: rgba(255,255,255,0.06) !important; }
  /* OrganizationProfile / Members page: search input and table cells ship
     with light-mode defaults even under the dark baseTheme, so paint them
     explicitly. */
  .cl-rootBox input:not([type="checkbox"]):not([type="radio"]),
  .cl-modalContent input:not([type="checkbox"]):not([type="radio"]) {
    background-color: #222 !important;
    color: #f1f5f9 !important;
    border-color: rgba(255,255,255,0.1) !important;
  }
  .cl-rootBox input::placeholder,
  .cl-modalContent input::placeholder { color: #64748b !important; }
  /* OTP verification code. The broad input override above repaints every
     Clerk input opaque (#222 bg, light text). For the OTP field that caused an
     auxiliary/hidden <input> to bleed its character through as one huge digit,
     so it was blanket-reset to transparent — but that ALSO blanked the visible
     digit cells (.cl-otpCodeFieldInput are real <input>s), making typed digits
     and the caret invisible in dark mode. Users reported "can't type the code".
     Fix: keep only NON-cell OTP inputs transparent (suppresses the bleed)... */
  .cl-rootBox .cl-otpCodeField input:not(.cl-otpCodeFieldInput):not([type="checkbox"]):not([type="radio"]),
  .cl-modalContent .cl-otpCodeField input:not(.cl-otpCodeFieldInput):not([type="checkbox"]):not([type="radio"]) {
    background-color: transparent !important;
    color: transparent !important;
    border-color: transparent !important;
  }
  /* ...while the visible digit cells render what the user types, with a
     visible caret and a 1px border (Tailwind border-* classes set color only). */
  .cl-rootBox .cl-otpCodeField input.cl-otpCodeFieldInput,
  .cl-modalContent .cl-otpCodeField input.cl-otpCodeFieldInput {
    background-color: #1a1a1a !important;
    color: #f1f5f9 !important;
    -webkit-text-fill-color: #f1f5f9 !important;
    caret-color: #f1f5f9 !important;
    border-width: 1px !important;
    border-style: solid !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }
  /* Members search input has an adjacent magnifier SVG that inherits
     currentColor. With no explicit text color on its wrapper it vanishes
     on dark backgrounds. */
  .cl-organizationProfileMembersSearchInputIcon,
  .cl-organizationProfileMembersSearchInputIcon path { color: #94a3b8 !important; fill: currentColor !important; }
  .cl-tableHead, .cl-tableHead th,
  .cl-tableHeadCell { color: #94a3b8 !important; border-color: rgba(255,255,255,0.06) !important; }
  .cl-tableBody td, .cl-tableRow td { color: #cbd5e1 !important; border-color: rgba(255,255,255,0.06) !important; }
  .cl-table, .cl-tableRow { border-color: rgba(255,255,255,0.06) !important; }
  /* Members / Invitations tabs at the top of the page. */
  .cl-tabListContainer { border-color: rgba(255,255,255,0.08) !important; }
  .cl-tabButton { color: #94a3b8 !important; }
  .cl-tabButton:hover { color: #e2e8f0 !important; }
  .cl-tabButton[aria-selected="true"] { color: #ffffff !important; border-color: #ffffff !important; }
  .cl-notificationBadge { background-color: rgba(255,255,255,0.08) !important; color: #e2e8f0 !important; border-color: rgba(255,255,255,0.1) !important; }
  /* Role pill / select trigger ("Member", "Admin"). Inner <span data-color="body">
     overrides the button's color, so target the span too. */
  .cl-selectButton, .cl-selectButton__role,
  button[role="combobox"] {
    color: #e2e8f0 !important;
    background-color: transparent !important;
    border-color: rgba(255,255,255,0.12) !important;
  }
  .cl-selectButton:hover, .cl-selectButton__role:hover { background-color: rgba(255,255,255,0.04) !important; }
  .cl-selectButton span, .cl-selectButton__role span { color: #e2e8f0 !important; }
  .cl-selectButton .cl-selectButtonIcon,
  .cl-selectButtonIcon__role,
  button[role="combobox"] svg { color: #94a3b8 !important; }
  /* Members + Invitations tables: pagination row. Clerk's baseTheme
     paints the page-number buttons in a near-background tone that
     reads as invisible on our #1a1a1a card. Force a slate body color
     for inactive digits, a brighter selected state, and visible
     prev/next arrows. */
  .cl-paginationRow,
  .cl-paginationRowText { color: #94a3b8 !important; }
  .cl-paginationButton,
  button[class*='cl-paginationButton'] {
    color: #cbd5e1 !important;
    background-color: transparent !important;
    border-color: rgba(255,255,255,0.1) !important;
  }
  .cl-paginationButton:hover,
  button[class*='cl-paginationButton']:hover {
    color: #ffffff !important;
    background-color: rgba(255,255,255,0.06) !important;
  }
  .cl-paginationButton[aria-current="true"],
  .cl-paginationButton[data-active="true"],
  button[class*='cl-paginationButton'][aria-current="true"],
  button[class*='cl-paginationButton'][data-active="true"] {
    color: #ffffff !important;
    background-color: rgba(255,255,255,0.1) !important;
    border-color: rgba(255,255,255,0.18) !important;
  }
  .cl-paginationButton:disabled,
  button[class*='cl-paginationButton']:disabled {
    color: #475569 !important;
    background-color: transparent !important;
  }
  .cl-paginationButtonIcon,
  .cl-paginationButton svg,
  button[class*='cl-paginationButton'] svg { color: currentColor !important; }
`;

const CLERK_DARK_OVERRIDE_ID = 'clerk-dark-override';

export const ThemedClerkProvider = ({ children }: { children: ReactNode }) => {
  const theme = useSelector(selectTheme);
  const [clerkTheme, setClerkTheme] = useState(
    theme === 'dark' ? customDarkTheme : undefined
  );

  useEffect(() => {
    setClerkTheme(theme === 'dark' ? customDarkTheme : undefined);
  }, [theme]);

  // Inject high-specificity dark mode styles for Clerk portals.
  useEffect(() => {
    let style = document.getElementById(
      CLERK_DARK_OVERRIDE_ID
    ) as HTMLStyleElement | null;
    if (theme === 'dark') {
      if (!style) {
        style = document.createElement('style');
        style.id = CLERK_DARK_OVERRIDE_ID;
        document.head.appendChild(style);
      }
      // Same layer as index.css's Clerk overrides, so specificity then order
      // decide between them. Left unlayered, every one of these !important
      // rules would lose to ANY layered !important rule.
      style.textContent = `@layer components {${CLERK_DARK_OVERRIDE_CSS}}`;
    } else if (style) {
      style.remove();
    }
  }, [theme]);

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={{
        theme: clerkTheme,
        // Emit Clerk's CSS into the `clerk` layer declared in index.css (above
        // preflight, below our overrides and utilities) — the precedence it had
        // under Tailwind v3, where Clerk's sheet loaded first.
        cssLayerName: 'clerk',
        // Align Clerk auth/profile forms to the portal — Lemlist blue primary +
        // rounder controls (12px) to match the restyle.
        variables: { colorPrimary: '#316bff', borderRadius: '12px' },
        elements:
          theme === 'dark'
            ? {
                card: 'bg-[#1a1a1a]! border-white/6!',
                headerTitle: 'text-slate-100',
                headerSubtitle: 'text-slate-400',
                socialButtonsBlockButton:
                  'bg-[#222]! border-white/6! hover:!bg-white/[0.08] text-slate-200!',
                formFieldLabel: 'text-slate-300!',
                formFieldInput:
                  'bg-[#222]! border-white/10! text-slate-100! placeholder:text-slate-500!',
                formButtonPrimary:
                  'bg-primary hover:bg-primary/90 text-primary-foreground',
                footerActionLink: 'text-primary hover:text-primary/80',
                footerActionText: 'text-slate-400',
                // Clerk hardcodes the impersonation pill's background to white
                // while its title follows the dark theme's light text, so
                // "Signed in as …" vanished. Paint the pill dark to match.
                impersonationFab: 'bg-[#1a1a1a]! border border-white/10',
                impersonationFabTitle: 'text-slate-300!',
              }
            : {},
        userProfile:
          theme === 'dark'
            ? {
                elements: {
                  card: 'bg-[#1a1a1a]! border-white/6!',
                  navbar: 'bg-[#1a1a1a]! border-white/6!',
                  navbarButton:
                    'text-slate-300! hover:!bg-white/[0.08] data-[active=true]:bg-white/8!',
                  navbarButtonIcon: 'text-slate-400!',
                  pageScrollBox: 'bg-[#1a1a1a]!',
                  page: 'text-slate-200!',
                  profileSectionTitle: 'text-slate-200! border-white/6!',
                  profileSectionTitleText: 'text-slate-200!',
                  profileSectionContent: 'text-slate-300!',
                  profileSectionPrimaryButton:
                    'text-slate-300! hover:!bg-white/[0.08]',
                  formFieldLabel: 'text-slate-300!',
                  formFieldInput: 'bg-[#222]! border-white/10! text-slate-100!',
                  formButtonPrimary:
                    '!bg-primary hover:bg-primary/90! text-primary-foreground!',
                  headerTitle: 'text-slate-100!',
                  headerSubtitle: 'text-slate-400!',
                  modalCloseButton: 'text-slate-400! hover:text-slate-200!',
                  badge: '!bg-white/[0.08] text-slate-300! border-white/10!',
                  footer: 'bg-[#1a1a1a]! border-white/6!',
                },
              }
            : {},
        organizationProfile:
          theme === 'dark'
            ? {
                elements: {
                  card: 'bg-[#1a1a1a]! border-white/6!',
                  navbar: 'bg-[#1a1a1a]! border-white/6!',
                  navbarButton:
                    'text-slate-300! hover:!bg-white/[0.08] data-[active=true]:bg-white/8!',
                  navbarButtonIcon: 'text-slate-400!',
                  pageScrollBox: 'bg-[#1a1a1a]!',
                  page: 'text-slate-200!',
                  profileSectionTitle: 'text-slate-200! border-white/6!',
                  profileSectionTitleText: 'text-slate-200!',
                  profileSectionContent: 'text-slate-300!',
                  profileSectionPrimaryButton:
                    'text-slate-300! hover:!bg-white/[0.08]',
                  formFieldLabel: 'text-slate-300!',
                  formFieldInput: 'bg-[#222]! border-white/10! text-slate-100!',
                  formButtonPrimary:
                    '!bg-primary hover:bg-primary/90! text-primary-foreground!',
                  headerTitle: 'text-slate-100!',
                  headerSubtitle: 'text-slate-400!',
                  modalCloseButton: 'text-slate-400! hover:text-slate-200!',
                  badge: '!bg-white/[0.08] text-slate-300! border-white/10!',
                  footer: 'bg-[#1a1a1a]! border-white/6!',
                },
              }
            : {},
      }}
      localization={{
        userButton: {
          action__manageAccount: 'Account settings',
        },
        signUp: {
          start: {
            title: 'Create your account',
            subtitle: 'Use your email to get started',
            actionText: 'Already have an account?',
            ...(IS_CLU && {
              alternativePhoneCodeProvider: WITH_PROVIDER_ON_CLU('Sign up'),
            }),
          },
          ...(IS_CLU && { emailLink: CONTINUE_TO_CLU }),
        },
        signIn: {
          start: {
            title: `Sign in to ${BRAND.name}`,
            subtitle: 'Use your email to continue',
            ...(IS_CLU && {
              titleCombined: `Continue to ${BRAND.dotName}`,
              alternativePhoneCodeProvider: WITH_PROVIDER_ON_CLU('Sign in'),
            }),
          },
          ...(IS_CLU && {
            alternativePhoneCodeProvider: CONTINUE_TO_CLU,
            emailCode: CONTINUE_TO_CLU,
            emailCodeMfa: CONTINUE_TO_CLU,
            emailLink: CONTINUE_TO_CLU,
            emailLinkMfa: CONTINUE_TO_CLU,
            phoneCode: CONTINUE_TO_CLU,
          }),
        },
        ...(IS_CLU && { organizationList: CONTINUE_TO_CLU }),
        formFieldLabel__emailAddress: 'Email',
        formFieldInputPlaceholder__emailAddress: 'you@example.com',
      }}
      afterSignOutUrl='/'
      signInUrl='/auth/login'
      signUpUrl='/auth/register'
      signInFallbackRedirectUrl='/'
      signUpFallbackRedirectUrl='/'
      {...SATELLITE_PROPS}
    >
      {children}
    </ClerkProvider>
  );
};
